import { useEffect, useCallback } from 'react';

type ModifierKey = 'meta' | 'ctrl' | 'alt' | 'shift';

interface ShortcutOptions {
  /** The key to listen for (e.g., 'k', 'Escape', 'Enter') */
  key: string;
  /** Modifier keys required (meta = Cmd on Mac, Ctrl on Windows) */
  modifiers?: ModifierKey[];
  /** Callback function when shortcut is triggered */
  callback: () => void;
  /** Whether the shortcut is enabled */
  enabled?: boolean;
  /** Prevent default browser behavior */
  preventDefault?: boolean;
}

/**
 * Hook to register global keyboard shortcuts
 *
 * @example
 * // Open search with Cmd/Ctrl+K
 * useKeyboardShortcut({
 *   key: 'k',
 *   modifiers: ['meta'],
 *   callback: () => openSearch(),
 * });
 */
export function useKeyboardShortcut({
  key,
  modifiers = [],
  callback,
  enabled = true,
  preventDefault = true,
}: ShortcutOptions) {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!enabled) return;

      // Check if the key matches (case-insensitive)
      const keyMatches = event.key.toLowerCase() === key.toLowerCase();
      if (!keyMatches) return;

      // Check modifier keys
      // 'meta' should match either metaKey (Mac Cmd) or ctrlKey (Windows/Linux Ctrl)
      const modifierChecks = {
        meta: modifiers.includes('meta') ? (event.metaKey || event.ctrlKey) : !(event.metaKey || event.ctrlKey),
        alt: modifiers.includes('alt') ? event.altKey : !event.altKey,
        shift: modifiers.includes('shift') ? event.shiftKey : !event.shiftKey,
      };

      // If 'ctrl' is explicitly required (separate from meta), check it
      if (modifiers.includes('ctrl') && !modifiers.includes('meta')) {
        modifierChecks.meta = event.ctrlKey;
      }

      const allModifiersMatch = Object.values(modifierChecks).every(Boolean);

      if (allModifiersMatch) {
        if (preventDefault) {
          event.preventDefault();
        }
        callback();
      }
    },
    [key, modifiers, callback, enabled, preventDefault]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, enabled]);
}

/**
 * Hook to register the global search shortcut (Cmd/Ctrl+K)
 */
export function useGlobalSearchShortcut(callback: () => void, enabled = true) {
  useKeyboardShortcut({
    key: 'k',
    modifiers: ['meta'],
    callback,
    enabled,
  });
}

export default useKeyboardShortcut;
